import '@sendbird/uikit-react/dist/index.css';

import { SendbirdProvider as SBSendbirdProvider } from '@sendbird/uikit-react/SendbirdProvider';
import type { SendbirdChatSDK } from '@sendbird/uikit-utils';
import React, { useState } from 'react';

import type { ContainerComponent } from '../../common/types/Common';
import { SENDBIRD_APP_ID } from '../../config/config';
import useGuideMessaging from '../hooks/useGuideMessaging';
import { SendbirdContext } from '../hooks/useSendbird';
import { IMESSAGE_BLUE, IMESSAGE_BLUE_DARK } from '../sendbird/LightUiKitTheme';
import { uiKitOptions } from '../sendbird/UiKitOptions';
import type { GuideMessagingDto, SendbirdSdk } from '../types/GuideMessaging';
import SendbirdSdkProvider from './SendbirdSdkProvider';

const SendbirdProvider: ContainerComponent = ({ children }) => {
    const { guideMessaging, isLoading: isLoadingGuideMessaging } =
        useGuideMessaging();
    const [sendbirdSdk, setSendbirdSdk] = useState<SendbirdSdk>({
        sdk: null as SendbirdChatSDK | null,
        isLoading: true,
    });

    const { user: sendbirdUser } = (guideMessaging as GuideMessagingDto) ?? {};
    const {
        userId: sendbirdUserId,
        accessToken,
        profileUrl,
    } = sendbirdUser ?? {};
    const hasMessagingAccount = guideMessaging != null;
    // Keep this tree stable. The first messaging account arrives while the
    // member is still on the booking screen, and a second return would remount
    // every screen under this provider before navigation can finish.
    const isLoading = hasMessagingAccount
        ? isLoadingGuideMessaging || sendbirdSdk.isLoading
        : isLoadingGuideMessaging;

    return (
        <SendbirdContext.Provider
            value={{
                sdk: hasMessagingAccount ? sendbirdSdk.sdk : null,
                isLoading,
            }}
        >
            <SBSendbirdProvider
                appId={SENDBIRD_APP_ID}
                userId={sendbirdUserId}
                accessToken={accessToken}
                profileUrl={profileUrl}
                colorSet={messagingColorTheme}
                uikitOptions={uiKitOptions}
            >
                <SendbirdSdkProvider
                    sendbirdSdk={sendbirdSdk}
                    setSendbirdSdk={setSendbirdSdk}
                >
                    {children}
                </SendbirdSdkProvider>
            </SBSendbirdProvider>
        </SendbirdContext.Provider>
    );
};

// Override default colors for Sendbird UIKit.
const messagingColorTheme = {
    '--sendbird-light-primary-500': IMESSAGE_BLUE_DARK,
    '--sendbird-light-primary-400': IMESSAGE_BLUE_DARK,
    '--sendbird-light-primary-300': IMESSAGE_BLUE,
    '--sendbird-light-primary-200': IMESSAGE_BLUE_DARK,
    '--sendbird-light-primary-100': IMESSAGE_BLUE_DARK,
};

export default SendbirdProvider;
